import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { VE_BANK_ASSETS } from '../../lib/brand';
import { VeeMascot } from '../VeeMascot';
import { LivePhone } from './LivePhone';
type Story = {
  title: string;
  body: string;
  surface: string;
  text: string;
  /** 'vee' anchors the assistant companion beside the live product preview. */
  media: 'vee' | 'product';
  screen: {
    path: string;
    label: string;
  };
  visual?: string;
  visualAlt?: string;
};

/** Existing landing feature items, order and copy preserved verbatim. */
const stories: Story[] = [{
  title: 'Intelligence that speaks your language.',
  body: 'Ask Vee about spending, transfers, bills, or an unfamiliar transaction. Vee prepares clear next steps, then steps back.',
  surface: 'bg-white',
  text: 'text-brand-navy',
  media: 'vee',
  screen: {
    path: '/chat',
    label: 'Vee workspace screen in the VE Bank app'
  }
}, {
  title: 'Your decision is the final step.',
  body: 'Every meaningful payment ends in an explicit review and your biometric confirmation.',
  surface: 'bg-[linear-gradient(145deg,#246BFE,#6747E8)]',
  text: 'text-white',
  media: 'product',
  screen: {
    path: '/review',
    label: 'Review and confirm screen in the VE Bank app'
  },
  visual: VE_BANK_ASSETS.biometricReview,
  visualAlt: 'Payment review panels beside a biometric confirmation control'
}, {
  title: 'The total is never a surprise.',
  body: 'Source account, recipient, amount, fee and total debit are visible together before confirmation.',
  surface: 'bg-white',
  text: 'text-brand-navy',
  media: 'product',
  screen: {
    path: '/draft',
    label: 'Prepared transfer draft screen in the VE Bank app'
  }
}];
export function StoryStack() {
  const reduced = useReducedMotion();
  return <div className="mx-auto max-w-6xl lg:px-10" data-id="element-116" data-mp="1ql1hccnvj6ci">
      <ul className="phone-scroll flex snap-x snap-mandatory overflow-x-auto px-5 pb-8 lg:block lg:overflow-visible lg:px-0 lg:pb-[16vh]" data-id="element-117" data-mp="24jhualcbxt33">
        {stories.map((story, index) => {
        const onDark = story.text === 'text-white';
        return <li key={story.title} className="w-[88vw] shrink-0 snap-center pr-5 last:pr-0 sm:w-[70vw] lg:w-auto lg:shrink lg:pr-0 lg:sticky lg:top-[92px]" data-id="element-118" data-mp="2cj5u1ztqsm4l" data-mpi={index}>
              <motion.article initial={reduced ? undefined : {
            opacity: 0,
            y: 40,
            scale: 0.98
          }} whileInView={reduced ? undefined : {
            opacity: 1,
            y: 0,
            scale: 1
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className={`relative h-full overflow-hidden rounded-[28px] p-6 shadow-[0_30px_80px_-42px_rgba(6,24,39,.55)] sm:p-10 lg:mb-[68px] lg:min-h-[66vh] lg:rounded-[34px] lg:p-14 ${story.surface} ${story.text}`} data-id="element-119" data-mp="2hgo4xvrblbf8">
                <div className="grid h-full items-center gap-8 lg:grid-cols-2 lg:gap-16" data-id="element-120" data-mp="15v8uxb89feq8">
                  <div className={`order-2 flex h-full flex-col justify-center ${index % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}`} data-id="element-121" data-mp="1atiwc3kkvbka">
                    <p className={`text-[10px] font-extrabold uppercase tracking-[.2em] ${onDark ? 'text-blue-200' : 'text-brand-cobalt'}`} data-id="element-122" data-mp="3o4vec5zil1bb">0{index + 1}</p>
                    <h3 className="mt-4 max-w-[18ch] text-[28px] font-extrabold leading-[1.05] tracking-[-.05em] sm:mt-5 sm:text-4xl lg:text-[44px] lg:leading-[1.02]" data-id="element-123" data-mp="3fbrv1h26tu8r">{story.title}</h3>
                    <p className={`mt-4 max-w-[42ch] text-sm leading-relaxed sm:mt-5 sm:text-base ${onDark ? 'text-blue-100' : 'text-brand-slate'}`} data-id="element-124" data-mp="3aaucv4h6h079">{story.body}</p>
                  </div>
                  <div className={`relative order-1 ${index % 2 === 1 ? 'lg:order-1' : 'lg:order-2'}`} data-id="element-125" data-mp="6tu92fub7bpb">
                    {story.visual && <img src={story.visual} alt={story.visualAlt || ''} loading="lazy" width={1440} height={1080} className="pointer-events-none absolute left-1/2 top-1/2 z-0 w-[112%] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-50" data-id="element-126" data-mp="3t3y82ua1nl3z" />}
                    <div className="relative z-10 mx-auto w-[78%] max-w-[280px] sm:w-[48%] lg:w-[70%] lg:max-w-[260px]" data-id="element-127" data-mp="e7l4upx7ljqa">
                      <LivePhone path={story.screen.path} label={story.screen.label} float={!reduced} data-id="element-128" data-mp="cisbjxuww4i" />
                    </div>
                    {story.media === 'vee' && <span className="absolute bottom-0 right-0 z-20 sm:right-6 lg:right-0" data-id="element-129" data-mp="t3smpjl5a92e">
                        <VeeMascot size={84} state="speaking" decorative data-id="element-130" data-mp="x4ckmpdjlgpl" />
                      </span>}
                  </div>
                </div>
              </motion.article>
            </li>;
      })}
      </ul>
    </div>;
}